"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 10;
      const blobs = heroRef.current.querySelectorAll<HTMLElement>('.hero-blob');
      blobs.forEach((blob, i) => {
        const factor = (i + 1) * 0.4;
        blob.style.transform = `translate(${x * factor}px, ${y * factor}px)`;
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden hero-gradient noise-overlay"
      style={{ paddingTop: '80px' }}
    >
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg" />

      {/* Atmospheric blobs */}
      <div className="hero-blob absolute top-[10%] right-[5%] w-[500px] h-[500px] rounded-full opacity-20 transition-transform duration-700 ease-out"
        style={{ background: 'radial-gradient(circle, #6AAF3D 0%, transparent 70%)' }} />
      <div className="hero-blob absolute bottom-[5%] left-[10%] w-[400px] h-[400px] rounded-full opacity-15 transition-transform duration-700 ease-out"
        style={{ background: 'radial-gradient(circle, #2472A4 0%, transparent 70%)' }} />
      <div className="hero-blob absolute top-[40%] left-[40%] w-[300px] h-[300px] rounded-full opacity-10 transition-transform duration-700 ease-out"
        style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }} />

      {/* Content */}
      <div className="relative z-10 max-w-8xl mx-auto px-6 lg:px-10 w-full py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Text */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full glass-card">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse-soft flex-shrink-0" />
              <span className="font-display text-xs font-700 tracking-widest text-white/90 uppercase" style={{ fontWeight: 700 }}>
                醫療器材專業供應商 · Medical Device Specialist
              </span>
            </div>

            {/* Headline */}
            <div className="space-y-3">
              <h1 className="font-display font-800 leading-[1.05] text-white" style={{ fontWeight: 800, fontSize: 'clamp(40px, 5.5vw, 72px)' }}>
                醫時醫療
                <br />
                <span style={{ color: '#9DCEF0' }}>有限公司</span>
              </h1>
              <p className="font-display text-white/60 font-500 tracking-[0.15em] uppercase" style={{ fontWeight: 500, fontSize: '18px' }}>
                Easy Medical Limited
              </p>
            </div>

            <p className="text-white/75 leading-relaxed max-w-lg" style={{ fontSize: '17px' }}>
              專業提供醫療器材行銷及服務，涵蓋<strong className="text-white/95">生命資訊與支援</strong>、<strong className="text-white/95">體外診斷</strong>及<strong className="text-white/95">醫學影像</strong>三大業務領域，為香港各醫療機構提供全方位設備解決方案。
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Link
                href="/products-services"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-display font-700 text-primary transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
                style={{ background: '#ffffff', fontWeight: 700, fontSize: '15px' }}
              >
                探索產品服務
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-display font-700 text-white glass-card transition-all duration-300 hover:bg-white/20"
                style={{ fontWeight: 700, fontSize: '15px' }}
              >
                立即聯絡我們
              </Link>
            </div>

            {/* Quick stats */}
            <div className="flex flex-wrap gap-8 pt-4 border-t border-white/10">
              {[
                { num: '3', label: '核心業務領域' },
                { num: '24/7', label: '技術支援服務' },
                { num: '港九新界', label: '服務範圍' },
              ].map((stat) => (
                <div key={stat.label} className="space-y-1">
                  <div className="font-display font-800 text-white" style={{ fontWeight: 800, fontSize: '28px' }}>{stat.num}</div>
                  <div className="text-white/55 text-xs font-500 uppercase tracking-wider" style={{ fontWeight: 500 }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Floating category cards */}
          <div className="relative hidden lg:block" style={{ height: '520px' }}>
            {/* Card 1 — Life Support */}
            <div className="absolute top-0 left-8 w-[260px] glass-card-white rounded-2xl p-5 shadow-card-hover animate-float-a card-hover"
              style={{ transform: 'rotate(-2deg)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #EBF3FA, #d0e8f7)' }}>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="font-display font-700 text-foreground text-sm mb-1" style={{ fontWeight: 700 }}>生命資訊與支援</h3>
              <p className="text-muted text-xs leading-relaxed">監視器 · 麻醉機 · 呼吸器 · 去顫儀</p>
              <div className="mt-3 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse-soft" />
                <span className="text-accent text-xs font-600" style={{ fontWeight: 600 }}>Life Information & Support</span>
              </div>
            </div>

            {/* Card 2 — IVD */}
            <div className="absolute top-[140px] right-0 w-[260px] glass-card-white rounded-2xl p-5 shadow-card-hover animate-float-b card-hover"
              style={{ transform: 'rotate(1.5deg)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #EEF7E6, #d6efc3)' }}>
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="font-display font-700 text-foreground text-sm mb-1" style={{ fontWeight: 700 }}>體外診斷</h3>
              <p className="text-muted text-xs leading-relaxed">血球 · 生化 · 化學發光分析儀及試劑</p>
              <div className="mt-3 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse-soft" />
                <span className="text-accent text-xs font-600" style={{ fontWeight: 600 }}>In Vitro Diagnostics</span>
              </div>
            </div>

            {/* Card 3 — Medical Imaging */}
            <div className="absolute bottom-0 left-20 w-[260px] glass-card-white rounded-2xl p-5 shadow-card-hover animate-float-c card-hover"
              style={{ transform: 'rotate(-1deg)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #EBF3FA, #d0e8f7)' }}>
                <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                </svg>
              </div>
              <h3 className="font-display font-700 text-foreground text-sm mb-1" style={{ fontWeight: 700 }}>醫學影像</h3>
              <p className="text-muted text-xs leading-relaxed">彩超 · 數位超音波設備</p>
              <div className="mt-3 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse-soft" />
                <span className="text-accent text-xs font-600" style={{ fontWeight: 600 }}>Medical Imaging</span>
              </div>
            </div>

            {/* Logo watermark */}
            <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 opacity-10">
              <AppImage
                src="/assets/images/image-2026-01-26-180338-1773297202078.png"
                alt="Easy Medical Limited logo"
                width={160}
                height={160}
                className="w-40 h-40 object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
        <span className="text-xs tracking-widest uppercase font-500" style={{ fontWeight: 500 }}>Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>
  );
}